import { useState, useCallback } from "react";
import { firebaseMessagingService } from "../services/firebaseMessagingService";
import { logException } from "@um/ui";

/**
 * Simple React hook wrapper for the Firebase messaging singleton service
 */
export const useFirebaseMessagingService = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const requestPermissionAndSaveToken = useCallback(
    async (userId: string): Promise<boolean> => {
      if (!userId) {
        setError("User ID is required");
        return false;
      }

      setIsLoading(true);
      setError(null);
      setSuccess(null);

      try {
        const success =
          await firebaseMessagingService.requestPermissionAndSaveToken(userId);
        if (!success) {
          setError("Failed to request permissions or save token");
        }
        setSuccess(
          success
            ? "Notifications enabled! Your character might check in soon."
            : null
        );
        return success;
      } catch (err: any) {
        logException(err);
        setError(err.message || "An error occurred");
        return false;
      } finally {
        setIsLoading(false);
      }
    },
    []
  );

  const getPermissionStatus = useCallback(async (): Promise<string> => {
    try {
      return await firebaseMessagingService.getPermissionStatus();
    } catch (err: any) {
      logException(err);
      console.error("Error getting permission status:", err);
      return "default";
    }
  }, []);

  return {
    requestPermissionAndSaveToken,
    getPermissionStatus,
    isLoading,
    error,
    success,
  };
};
